import { useCallback, useEffect, useState } from 'react';

import { useNavigationStack } from '../../../../context/NavigationStackContext';
import { useUserConfig } from '../../../../context/UserConfigContext';
import { useUpdateMyConfig } from '../../../../redux/api/users/usersApi';
import {
  TrainingSetItemStatusListMode,
  UserConfigKey,
} from '../../../../shared/constants';
import { Paths } from '../../../../shared/paths';

export function useFunctionalityEditComponent() {
  const { config, options } = useUserConfig();
  const { replace } = useNavigationStack();
  const [updateMyConfig, { isLoading }] = useUpdateMyConfig();
  const [showSaveSetButton, setShowSaveSetButton] = useState(false);
  const [showUploadSetButton, setShowUploadSetButton] = useState(false);
  const [showExerciseUnits, setShowExerciseUnits] = useState(true);
  const [onboardingEnabled, setOnboardingEnabled] = useState(true);
  const [showTooltips, setShowTooltips] = useState(true);
  const [trainingSetItemStatusListMode, setTrainingSetItemStatusListMode] =
    useState<TrainingSetItemStatusListMode>(TrainingSetItemStatusListMode.all);
  const [defaultSetItemRestSec, setDefaultSetItemRestSec] = useState<
    number | null
  >(null);

  useEffect(() => {
    if (config) {
      setShowSaveSetButton(options.showSaveSetButton);
      setShowUploadSetButton(options.showUploadSetButton);
      setShowExerciseUnits(options.showExerciseUnits);
      setOnboardingEnabled(options.onboardingEnabled);
      setShowTooltips(options.showTooltips);
      setTrainingSetItemStatusListMode(options.trainingSetItemStatusListMode);
      setDefaultSetItemRestSec(options.defaultSetItemRestSec);
    }
  }, [config, options]);

  const handleSave = useCallback(() => {
    const restSec =
      defaultSetItemRestSec === null
        ? null
        : Math.min(300, Math.max(0, defaultSetItemRestSec));
    updateMyConfig({
      config: [
        {
          key: UserConfigKey.showSaveSetButton,
          value: String(showSaveSetButton),
        },
        {
          key: UserConfigKey.showUploadSetButton,
          value: String(showUploadSetButton),
        },
        {
          key: UserConfigKey.showExerciseUnits,
          value: String(showExerciseUnits),
        },
        {
          key: UserConfigKey.onboardingEnabled,
          value: String(onboardingEnabled),
        },
        {
          key: UserConfigKey.showTooltips,
          value: String(showTooltips),
        },
        {
          key: UserConfigKey.trainingSetItemStatusListMode,
          value: trainingSetItemStatusListMode,
        },
        {
          key: UserConfigKey.defaultSetItemRestSec,
          value: restSec === null ? null : String(restSec),
        },
      ],
    })
      .unwrap()
      .then(() => {
        replace(Paths.settings.toFunctionalityView);
      });
  }, [
    showSaveSetButton,
    showUploadSetButton,
    showExerciseUnits,
    onboardingEnabled,
    showTooltips,
    trainingSetItemStatusListMode,
    defaultSetItemRestSec,
    updateMyConfig,
    replace,
  ]);

  const isDirty =
    showSaveSetButton !== options.showSaveSetButton ||
    showUploadSetButton !== options.showUploadSetButton ||
    showExerciseUnits !== options.showExerciseUnits ||
    onboardingEnabled !== options.onboardingEnabled ||
    showTooltips !== options.showTooltips ||
    trainingSetItemStatusListMode !== options.trainingSetItemStatusListMode ||
    defaultSetItemRestSec !== options.defaultSetItemRestSec;

  return {
    showSaveSetButton,
    setShowSaveSetButton,
    showUploadSetButton,
    setShowUploadSetButton,
    showExerciseUnits,
    setShowExerciseUnits,
    onboardingEnabled,
    setOnboardingEnabled,
    showTooltips,
    setShowTooltips,
    trainingSetItemStatusListMode,
    setTrainingSetItemStatusListMode,
    defaultSetItemRestSec,
    setDefaultSetItemRestSec,
    handleSave,
    isLoading,
    isDirty,
  };
}
